import { Outlet, useOutletContext } from 'react-router'
import { toast } from 'sonner'
import type { ServiceCursorPaginatedResponseType } from '@/connect-types/backend/service'
import type { OrganizationUserGiftType } from '@/state/entities/organisation-user/organisation-user.types'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import type { InteractionType } from '@/state/morpheus/types/interaction'
import type { OrganizationRegistration } from '@/state/morpheus/types/organization_registration'
import type { ReviewResponseType } from '@/state/morpheus/types/reviews'
import type {
  OrgProfileTagTableType,
  ProfileMarketingEventType,
  ProfileTagsType,
  ProfileTransactionType,
} from '@/state/morpheus/types/segments/customers'
import { type Booking } from '@/state/zion/types'
import { buildUrl, getFirst } from '@/utils/common'
import { type CustomQuestionAnswer } from '@/types/customer'

interface CustomerCountsType {
  _count: {
    custom_question_answers: number
    gift_card: number
    // organization_profile_marketing_events: number
    organization_profile_tags: number
    organization_profile_transaction: number
    organization_registration_personalisation_choice: number
    organization_utm_conversion: number
    user_booking: number
    user_review: number
    // website_trackable_profiles: number
  }
}

export interface CustomerOverviewType {
  created_at: string
  custom_question_answers: CustomQuestionAnswer[]
  data_opt_in_at: string | null | undefined
  email_opt_in_at: string | null | undefined
  id: string
  last_interacted_at: string | null | undefined
  organization_id: string
  organization_profile_tags: {
    id: string
    organization_tag: {
      name: string
    }
    tag_id: string
  }[]

  organization_registration_personalisation_choice: {
    choice: number
    created_at: string
    organization_personalisation_cards: {
      id: string
      image: string
      question: string
    }
  }[]
  profile_id: number
  sms_opt_in_at: string | null | undefined
  user_profile: {
    email: string
    first: string
    id: number
    last: string
    lat: number | null
    lng: number | null
    phone: string
    birth_month: number | null
    birth_day: number | null
    postcode: string | null | undefined
    verified: 1 | 0 | null
  }

  visits: {
    _max: {
      last_interacted_at: string
    }
    _sum: {
      interactions: number
    }
  }
  transactions: {
    _sum: {
      penny_value: number
    }
  }
}
interface SimpleQueryType {
  limit: number
  cursor: string
}

// Order types
export interface OrderLineItem {
  id: string
  transaction_id: string
  name: string
  description: string | null
  sku: string | null
  category: string | null
  quantity: number
  unit_amount: number
  amount: number
  external_line_item_id: string | null
  catalog_object_id: string | null
  variation_name: string | null
  created_at: string
}

export interface OrderTransaction {
  id: string
  organization_registration_id: string
  currency: string
  interaction_id: string
  penny_value: number
  created_at: string
  line_items: OrderLineItem[]
}

export interface OrderInteraction {
  id: string
  organization_id: string
  data_source_id: string
  created_at: string
  ended_at: string | null
  currency: string
  penny_value: number
  data_source: {
    id: string
    key: string
    name: string
    is_visit: number
    is_transaction: number
  }
  interaction_serial: {
    id: string
    interaction_id: string
    serial: string
    location_settings: {
      alias: string
      serial: string
    }
  }[]
  organization_profile_transaction: OrderTransaction[]
}

export interface OrderCounts {
  total_orders: number
  online_orders: number
  in_venue_orders: number
  total_value: number
  total_items: number
}

// Customer Explain types
export interface CustomerExplainTopItem {
  name: string
  times_ordered: number
}

export interface CustomerExplainMostExpensiveItem {
  name: string
  price: number // in pennies
}

export interface CustomerExplainRecentReview {
  rating: number | null
  sentiment: string | null // positive, negative, neutral, mixed
  platform: string | null // google, stampede, tripadvisor, etc.
  text: string | null
  created_at: string // ISO 8601
}

export interface CustomerExplainRecentActivity {
  orders_this_month: number
  orders_last_month: number
  last_order_date: string | null
  wifi_this_month: number
  last_wifi_visit: string | null
}

export interface CustomerExplainTags {
  vip: string[]
  dietary: string[]
  other: string[]
}

export interface CustomerExplainForecasting {
  current_annual_value: number // in pennies
  one_more_visit_monthly_increase: number // in pennies
  double_frequency_value: number // in pennies
}

export interface CustomerExplainQuickFacts {
  visit_pattern: string
  total_value: string
  loyalty: string
}

export interface CustomerExplainFavorites {
  top_item: string | null
  common_pairing: string | null
  category: string | null
}

export interface CustomerExplainPreferences {
  dietary: string | null
  special_notes: string | null
}

export interface CustomerExplainEngagement {
  reviews: string | null
  venues: string | null
  marketing: string
}

export interface CustomerExplainInsights {
  friendly_summary: string
  quick_facts: CustomerExplainQuickFacts
  favorites: CustomerExplainFavorites
  preferences: CustomerExplainPreferences
  engagement: CustomerExplainEngagement
  server_tips: string[]
}

export interface CustomerExplainSummary {
  name: string
  total_orders: number
  total_spent: number // in pennies
  average_order_value: number // in pennies
  average_item_price: number // in pennies
  orders_per_month: number
  months_as_customer: number
  recent_activity: CustomerExplainRecentActivity
  bookings: number
  gift_cards: number
  reviews: number
  wifi_connections: number
  member_since: string // ISO 8601 timestamp
  last_visit: string | null // ISO 8601 timestamp
  forecasting: CustomerExplainForecasting
  top_items: CustomerExplainTopItem[]
  most_expensive_item: CustomerExplainMostExpensiveItem
  tags: CustomerExplainTags
  wifi_venues: string[]
  recent_reviews: CustomerExplainRecentReview[]
}

export interface CustomerExplainResponse {
  insights: CustomerExplainInsights
  summary: CustomerExplainSummary
}

const morpheusApiwithTags = morpheusApi.enhanceEndpoints({
  addTagTypes: ['CustomerQuery'],
})

export const customerApi = morpheusApiwithTags.injectEndpoints({
  endpoints: (build) => ({
    getCustomers: build.query<
      ServiceCursorPaginatedResponseType<OrganizationRegistration>,
      {
        orgId: string
        search: string
        query: { limit: number; cursor: string; serial: string; tag_id: string }
      }
    >({
      query: ({ orgId, search, query }) => ({
        method: 'GET',
        url: buildUrl(`/organisations/${orgId}/crm/customers`, {
          search,
          ...query,
        }),
      }),
    }),
    getOrgRegUsingProfileId: build.query<
      OrganizationRegistration,
      { orgId: string; profileId: number }
    >({
      query: ({ orgId, profileId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/customers/profile/${profileId}`,
      }),
    }),
    getCustomer: build.query<
      CustomerOverviewType,
      { orgId: string; customerId: string }
    >({
      query: ({ orgId, customerId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/customers/${customerId}`,
      }),
      providesTags: (data: any) =>
        data ? [{ type: 'CustomerQuery', id: data?.id }] : [],
    }),
    getCustomerCounts: build.query<
      CustomerCountsType,
      { orgId: string; customerId: string }
    >({
      query: ({ orgId, customerId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/customers/${customerId}/count`,
      }),
    }),
    getCustomerGifts: build.query<
      ServiceCursorPaginatedResponseType<OrganizationUserGiftType>,
      { orgId: string; customerId: string; query: SimpleQueryType }
    >({
      query: ({ orgId, customerId, query }) => ({
        method: 'GET',
        url: buildUrl(
          `/organisations/${orgId}/crm/customers/${customerId}/gift`,
          query
        ),
      }),
    }),
    getCustomerMarketing: build.query<
      ServiceCursorPaginatedResponseType<ProfileMarketingEventType>,
      { orgId: string; customerId: string; query: SimpleQueryType }
    >({
      query: ({ orgId, customerId, query }) => ({
        method: 'GET',
        url: buildUrl(
          `/organisations/${orgId}/crm/customers/${customerId}/marketing`,
          query
        ),
      }),
    }),
    getCustomerOrders: build.query<
      ServiceCursorPaginatedResponseType<OrderInteraction>,
      { orgId: string; customerId: string; query: SimpleQueryType }
    >({
      query: ({ orgId, customerId, query }) => ({
        method: 'GET',
        url: buildUrl(
          `/organisations/${orgId}/crm/customers/${customerId}/orders`,
          query
        ),
      }),
    }),
    getCustomerOrdersCount: build.query<
      OrderCounts,
      { orgId: string; customerId: string }
    >({
      query: ({ orgId, customerId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/customers/${customerId}/orders/count`,
      }),
    }),
    getCustomerExplain: build.query<
      CustomerExplainResponse,
      { orgId: string; customerId: string }
    >({
      query: ({ orgId, customerId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/customers/${customerId}/explain`,
      }),
      providesTags: (result, error, { customerId }) => [
        { type: 'CustomerQuery', id: customerId },
      ],
    }),
    getCustomerTransactions: build.query<
      ServiceCursorPaginatedResponseType<ProfileTransactionType>,
      { orgId: string; customerId: string; query: SimpleQueryType }
    >({
      query: ({ orgId, customerId, query }) => ({
        method: 'GET',
        url: buildUrl(
          `/organisations/${orgId}/crm/customers/${customerId}/transactions`,
          query
        ),
      }),
    }),
    getCustomerActivity: build.query<
      ServiceCursorPaginatedResponseType<InteractionType>,
      { orgId: string; customerId: string; query: SimpleQueryType }
    >({
      query: ({ orgId, customerId, query }) => ({
        method: 'GET',
        url: buildUrl(
          `/organisations/${orgId}/crm/customers/${customerId}/activity`,
          query
        ),
      }),
    }),
    getCustomerMostRecent: build.query<
      (
        | ProfileMarketingEventType
        | ReviewResponseType
        | OrganizationUserGiftType
        | Booking
      )[],
      { orgId: string; customerId: string }
    >({
      query: ({ orgId, customerId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/customers/${customerId}/activity/recent`,
      }),
    }),
    getCustomerRoi: build.query<
      { data: { month: string; amount: number }[]; currency: string },
      { orgId: string; customerId: string }
    >({
      query: ({ orgId, customerId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/customers/${customerId}/activity/roi`,
      }),
    }),
    getCustomerTags: build.query<
      ServiceCursorPaginatedResponseType<ProfileTagsType>,
      { orgId: string; customerId: string }
    >({
      query: ({ orgId, customerId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/customers/${customerId}/tags`,
      }),
    }),

    addTagToCustomer: build.mutation<
      ProfileTagsType[],
      { orgId: string; tag: { name: string }; customerIds: string[] }
    >({
      query: ({ orgId, tag, customerIds }) => ({
        url: `/organisations/${orgId}/crm/customers/${getFirst(customerIds)}/tags`,
        method: 'put',
        data: { ...tag, customerIds },
      }),
      invalidatesTags: (_1, _3, item) => [
        ...item?.customerIds.map((item: any) => ({
          id: item,
          type: 'CustomerQuery' as const,
        })),
      ],
      transformResponse(item: ProfileTagsType[]) {
        toast.success('Tag added to customer(s)')
        return item
      },
    }),

    removeTagFromCustomer: build.mutation<
      OrgProfileTagTableType,
      { profileTagId: string; orgId: string; customerId: string }
    >({
      query: ({ orgId, profileTagId, customerId }) => ({
        url: `/organisations/${orgId}/crm/customers/${customerId}/tags/${profileTagId}`,
        method: 'delete',
      }),
      invalidatesTags: (_1, _3, item) => [
        { id: item?.customerId, type: 'CustomerQuery' },
      ],
      transformResponse(item: OrgProfileTagTableType) {
        toast.success('Tag removed from customer')
        return item
      },
    }),

    disableCustomerEmailOptIn: build.mutation<
      {
        message: string
        organisation_registration: unknown
      },
      { orgId: string; orgRegId: string }
    >({
      query: ({ orgId, orgRegId }) => ({
        url: `/organisations/${orgId}/crm/customers/${orgRegId}/disable-email-opt-in`,
        method: 'PUT',
      }),
      transformResponse(data: {
        message: string
        organisation_registration: unknown
      }) {
        toast.success(data.message)
        return data
      },
      transformErrorResponse() {
        toast.error('Failed to disable email opt-in')
      },
      invalidatesTags: (_result, _error, { orgRegId }) => [
        { type: 'CustomerQuery', id: orgRegId },
      ],
    }),

    disableCustomerSmsOptIn: build.mutation<
      {
        message: string
        organisation_registration: unknown
      },
      { orgId: string; orgRegId: string }
    >({
      query: ({ orgId, orgRegId }) => ({
        url: `/organisations/${orgId}/crm/customers/${orgRegId}/disable-sms-opt-in`,
        method: 'PUT',
      }),
      transformResponse(data: {
        message: string
        organisation_registration: unknown
      }) {
        toast.success(data.message)
        return data
      },
      transformErrorResponse() {
        toast.error('Failed to disable SMS opt-in')
      },
      invalidatesTags: (_result, _error, { orgRegId }) => [
        { type: 'CustomerQuery', id: orgRegId },
      ],
    }),
  }),
})

function CustomerSingleLayout() {
  return <Outlet />
}

export const useCustomerProfileContext = () => {
  return useOutletContext<CustomerOverviewType>()
}

export default CustomerSingleLayout
